import { useState, useRef, useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet-async'
import { CheckCircle, Landmark, Clock } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import { useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'

const ConsultingSuccess = () => {
  const { language } = useLanguage()
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const orderNumber = searchParams.get('order') || `THK-${Date.now()}`

  const content = {
    tr: {
      pageTitle: 'Sipariş Alındı',
      heading: 'Siparişiniz başarıyla oluşturuldu!',
      subheading:
        'Ödeme bilgileri aşağıda yer alıyor. Havale/EFT işlemini tamamladıktan sonra dekontu bizimle paylaşabilirsiniz.',
      status: 'Ödeme Bekleniyor',
      instructions:
        'Lütfen havale/EFT açıklamasına sipariş numaranızı ekleyin. Ödemeniz ulaştığında danışmanımız sizinle iletişime geçecektir.',
      back: 'Danışmanlık Sayfasına Dön',
      bankTitle: 'Banka Hesap Bilgileri'
    },
    en: {
      pageTitle: 'Order Received',
      heading: 'Your order has been created!',
      subheading:
        'Bank transfer details are listed below. You can share the payment receipt with us once you complete the transfer.',
      status: 'Awaiting Payment',
      instructions:
        'Please include your order number in the payment description. Our consultant will contact you once the payment is received.',
      back: 'Return to Consulting Page',
      bankTitle: 'Bank Account Details'
    },
    ru: {
      pageTitle: 'Заказ получен',
      heading: 'Ваш заказ был успешно создан!',
      subheading:
        'Реквизиты для банковского перевода указаны ниже. После оплаты отправьте нам подтверждение платежа.',
      status: 'Ожидание оплаты',
      instructions:
        'Укажите номер заказа в назначении платежа. После получения оплаты наш консультант свяжется с вами.',
      back: 'Вернуться на страницу консультаций',
      bankTitle: 'Банковские реквизиты'
    }
  }[language as 'tr' | 'en' | 'ru']

  const bankInfo = useMemo(() => (
    {
      tr: {
        bankName: 'Banka: Garanti BBVA',
        branchName: 'Şube: Şirinevler',
        accountHolderLabel: 'Hesap Sahibi',
        accountHolder: 'THINKLYZE BİLGİ TEKNOLOJİLERİ REKLAMCILIK BİLİŞİM DANIŞMANLIĞI LİMİTED ŞİRKETİ',
        ibanLabel: 'IBAN',
        iban: 'TR11 0006 2000 1870 0006 2909 11',
        copyAccountHolder: 'Hesap sahibini kopyala',
        copyIban: 'IBAN\'ı kopyala',
        copiedAccount: 'Hesap sahibi kopyalandı!',
        copiedIban: 'IBAN kopyalandı!'
      },
      en: {
        bankName: 'Bank: Garanti BBVA',
        branchName: 'Branch: Şirinevler',
        accountHolderLabel: 'Account Holder',
        accountHolder: 'THINKLYZE BİLGİ TEKNOLOJİLERİ REKLAMCILIK BİLİŞİM DANIŞMANLIĞI LİMİTED ŞİRKETİ',
        ibanLabel: 'IBAN',
        iban: 'TR11 0006 2000 1870 0006 2909 11',
        copyAccountHolder: 'Copy account holder',
        copyIban: 'Copy IBAN',
        copiedAccount: 'Account holder copied!',
        copiedIban: 'IBAN copied!'
      },
      ru: {
        bankName: 'Банк: Garanti BBVA',
        branchName: 'Отделение: Шириневлер',
        accountHolderLabel: 'Получатель',
        accountHolder: 'THINKLYZE BİLGİ TEKNOLOJİLERİ REKLAMCILIK BİLİŞİM DANIŞMANLIĞI LİMİTED ŞİRKETİ',
        ibanLabel: 'IBAN',
        iban: 'TR11 0006 2000 1870 0006 2909 11',
        copyAccountHolder: 'Скопировать получателя',
        copyIban: 'Скопировать IBAN',
        copiedAccount: 'Получатель скопирован!',
        copiedIban: 'IBAN скопирован!'
      }
    }[language as 'tr' | 'en' | 'ru']
  ), [language])

  const [copiedField, setCopiedField] = useState<'account' | 'iban' | null>(null)
  const copyTimeoutRef = useRef<number | null>(null)

  const handleCopy = (value: string, field: 'account' | 'iban') => {
    const copyValue = async () => {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(value)
        } else {
          const textarea = document.createElement('textarea')
          textarea.value = value
          textarea.style.position = 'fixed'
          textarea.style.opacity = '0'
          document.body.appendChild(textarea)
          textarea.focus()
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
        }

        setCopiedField(field)
        if (copyTimeoutRef.current) {
          window.clearTimeout(copyTimeoutRef.current)
        }
        copyTimeoutRef.current = window.setTimeout(() => setCopiedField(null), 2000)
      } catch (error) {
        console.error('Copy failed', error)
      }
    }

    copyValue()
  }

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        window.clearTimeout(copyTimeoutRef.current)
      }
    }
  }, [])

  return (
    <>
      <Helmet>
        <title>{content.pageTitle} - Thinklyze</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-[#f9f9f9] pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-3xl shadow-lg border border-gray-100 p-10"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-3">
                {content.heading}
              </h1>
              <p className="text-gray-600 max-w-2xl">
                {content.subheading}
              </p>

              <div className="w-full mt-8 p-6 bg-blue-50 border border-blue-100 rounded-2xl text-left">
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-semibold text-blue-700 uppercase tracking-wide">
                    {content.status}
                  </span>
                </div>
                <div className="mb-4">
                  <span className="inline-block text-sm font-medium text-blue-700 bg-white/60 border border-blue-200 rounded-full px-3 py-1">
                    Sipariş No: {orderNumber}
                  </span>
                </div>
                <p className="mt-4 text-sm text-blue-900 leading-relaxed whitespace-pre-line">
                  {content.instructions}
                </p>
              </div>

              <div className="w-full mt-8 text-left">
                <div className="flex items-center gap-3 mb-4">
                  <Landmark className="w-6 h-6 text-gray-900" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    {content.bankTitle}
                  </h2>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
                  <p className="text-sm text-gray-800 font-mono whitespace-pre-line">
                    {`${bankInfo.bankName}\n${bankInfo.branchName}`}
                  </p>
                  <div className="mt-4 space-y-3">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <p className="text-sm text-gray-800 font-mono">
                        {`${bankInfo.accountHolderLabel}: ${bankInfo.accountHolder}`}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleCopy(bankInfo.accountHolder, 'account')}
                        className="self-start sm:self-auto inline-flex items-center gap-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {bankInfo.copyAccountHolder}
                      </button>
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <p className="text-sm text-gray-800 font-mono">
                        {`${bankInfo.ibanLabel}: ${bankInfo.iban}`}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleCopy(bankInfo.iban, 'iban')}
                        className="self-start sm:self-auto inline-flex items-center gap-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {bankInfo.copyIban}
                      </button>
                    </div>
                    {copiedField === 'account' && (
                      <span className="text-xs text-green-600">{bankInfo.copiedAccount}</span>
                    )}
                    {copiedField === 'iban' && (
                      <span className="text-xs text-green-600">{bankInfo.copiedIban}</span>
                    )}
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate('/consulting')}
                className="mt-10 inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-full hover:bg-black transition-colors"
              >
                {content.back}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  )
}

export default ConsultingSuccess















